<template>
  <dx-modal title="Lista de serviços, taxas e outras despesas"
            size="lg"
            :value="true"
            @input="close">
    <div class="loading loading-lg mt-2" v-if="loading" />
    <template v-else>
      <div class="columns">
        <div class="column col-3 col-md-4 col-sm-12 form-group">
          <label class="form-label">Tipo</label>
          <select class="form-select"
                  v-model="filter.type"
                  @change="find"
          >
            <option value="">Todos os tipos</option>
            <option v-for="(text, value) in expenseTypes"
                    :value="value"
                    :key="value"
            >{{ text }}
            </option>
          </select>
        </div>
        <div class="column form-group">
          <label class="form-label">Pesquisar</label>
          <div class="input-group">
            <input
              type="text"
              class="form-input"
              id="search"
              v-model="filter.search"
              v-focus
              @keydown.enter="find"
              placeholder="Digite parte do nome ou código e tecle [Enter]"
            >
            <button
              class="btn btn-neutral btn-icon btn-action input-group-btn"
              :disabled="loading"
              :class="{loading}"
              @click="find"
            ><fa-icon :icon="['fal', 'search']" />
            </button>
          </div>
        </div>
        <div class="column col-auto form-group" style="align-self: flex-end">
          <router-link
            class="btn btn-secondary btn-icon btn-icon-left mb-2"
            to="/clinical/expenses"
            target="_blank"
            rel="noopener noreferrer">
            <fa-icon :icon="['fal', 'plus']" />Novo serviço
          </router-link>
        </div>
      </div>
      <div class="empty mt-2"
           v-if="data.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h6">Serviços, taxas e outras despesas</p>
        <p class="empty-subtitle">
          Nenhum serviço encontrado. Verifique os filtros para realizar novas buscas.
        </p>
      </div>
      <table class="table table-hover expense-modal-table" v-else>
        <thead>
        <tr>
          <th class="text-secondary" style="width: 40px">
            <label class="form-checkbox">
              <input type="checkbox"
                     @change="selectAllExpenses"
                     :checked="getExpenseSelected"
                     :indeterminate.prop="getExpenseSelected === null">
              <i class="form-icon"></i>
            </label>
          </th>
          <th>Serviços cadastrados</th>
          <th>TUSS</th>
          <th>Tipo</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, i) in data.items" :key="i">
          <td class="text-center">
            <label class="form-checkbox">
              <input type="checkbox" v-model="item.selected">
              <i class="form-icon" />
            </label>
          </td>
          <td>
            <span class="hover-link c-hand" @click="selected(item)">
              {{ item.name }}
            </span>
          </td>
          <td>
            {{ item?.tuss?.code | tuss }}
          </td>
          <td>{{ getTypeName(item.type) }}</td>
        </tr>
        </tbody>
      </table>
      <div class="load-more" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais...</button>
      </div>
    </template>
    <template v-slot:footer>
      <button
        class="btn btn-primary btn-icon btn-icon-left mr-2"
        v-if="hasSelected"
        @click="addMultiSelected">
        <fa-icon :icon="['fal', 'check-double']" />
        Adicionar selecionados
      </button>
      <button class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { types as expenseTypes, getName as getTypeName } from '@/data/expense-types';

export default {
  data() {
    return {
      loading: false,
      loadingMore: false,
      filter: {
        type: '',
        search: '',
      },
      expenseTypes,
      getTypeName,
      data: this.blankData(),
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    hasSelected() {
      return this.data.items.some(({ selected }) => selected);
    },
    getExpenseSelected() {
      if (!this.data.items || this.data.items.length === 0) {
        return false;
      }

      const selected = this.data.items.filter(item => item.selected);

      if (selected.length === 0) {
        return false;
      }
      if (selected.length === this.data.items.length) {
        return true;
      }
      return null;
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    find() {
      this.data.items = [];
      this.data.hasMore = false;
      this.data.offset = 0;
      this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        active: true,
        limit: this.data.limit,
        offset: this.data.offset,
      };

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.search) {
        params.search = this.filter.search;
      }

      return this.$httpX.get('/expenses', { params })
        .then(({ data }) => {
          data.items.forEach((item) => {
            item.selected = false;
            return item;
          });

          this.data.hasMore = data.hasMore;
          this.data.items = firstData
            ? data.items
            : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    loadMore() {
      this.data.offset += this.data.limit;
      this.load();
    },
    selectAllExpenses(e) {
      this.data.items.forEach((item) => {
        item.selected = e.target.checked;
      });
    },
    selected(item) {
      this.$emit('selected', [item]);
    },
    addMultiSelected() {
      this.$emit('selected', this.data.items.filter(({ selected }) => selected));
    },
    blankData() {
      return {
        items: [],
        hasMore: false,
        limit: 200,
        offset: 0,
      };
    },
  },
};
</script>
